
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { BASE_URL } from '@/config/api';

const ForgotPassword = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Helper function to convert potential array translations to string
  const translateStr = (key: string): string => {
    const translated = t(key);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${BASE_URL}/api/forgot-password`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: email }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(t('forgotPassword.error'));
      }
      
      setIsSuccess(true);
      toast({
        title: translateStr('forgotPassword.successTitle'),
        description: translateStr('forgotPassword.successDescription'),
      });
    } catch (error) {
      console.error('Forgot password error:', error);
      toast({
        variant: 'destructive',
        title: translateStr('forgotPassword.errorTitle'),
        description: error instanceof Error ? error.message : translateStr('forgotPassword.errorGeneric'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center p-8 md:p-12 lg:p-16">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8">
            <Link 
              to="/login" 
              className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span>{t('common.backToLogin')}</span>
            </Link>
          </div>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {t('forgotPassword.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('forgotPassword.subtitle')}
            </p>
          </div>
          
          {isSuccess ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-green-800">{t('forgotPassword.checkEmail')}</h3>
              <p className="mt-2 text-sm text-green-700">{t('forgotPassword.emailSent')}</p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium">
                  {t('forgotPassword.username')}
                </label>
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  className="w-full p-3 rounded-lg"
                />
              </div>
              
              <div className="pt-2">
                <Button 
                  type="submit" 
                  className="w-full py-6 text-base" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {translateStr('common.loading')}
                    </>
                  ) : (
                    translateStr('forgotPassword.resetButton')
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
      
      {/* Right side - Image */}
      <div className="hidden md:block flex-1 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 opacity-90" />
        <div className="absolute inset-0 bg-[url('https://storage.googleapis.com/sportlogin/frontend/images/3re04z6e8f06sfd.avif')] bg-cover bg-center opacity-20" />
        
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="max-w-md text-white text-center">
            <div className="mb-6">
              <span className="inline-block bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                Coach Premium plan
              </span>
            </div>
            <h2 className="text-3xl font-bold mb-4">
              {translateStr('forgotPassword.sidebarTitle')}
            </h2>
            <p className="text-white/80">
              {translateStr('forgotPassword.sidebarText')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
